/**
 * Customer Accounts — JS para páginas de conta
 * - Máscara e validação de CPF/CNPJ no registro
 * - Máscara de telefone no registro
 * - Toggle login/recuperação de senha
 * - Toggle de formulários de endereço
 */

class CustomerAccounts {
    constructor() {
        const path = window.location.pathname;

        /* Password visibility toggle — works on all account pages */
        this.#initPasswordToggles();

        if (path.includes('/account/register')) {
            this.#initRegister();
        } else if (path.includes('/account/login')) {
            this.#initLogin();
        } else if (path.includes('/account/addresses')) {
            this.#initAddresses();
        }
    }

    /* =========================================
       PASSWORD TOGGLE — Mostrar/Ocultar Senha
       ========================================= */

    #initPasswordToggles() {
        document.querySelectorAll('[data-toggle-password]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const targetId = /** @type {HTMLElement} */ (btn).dataset.togglePassword;
                if (!targetId) return;

                const input = /** @type {HTMLInputElement | null} */ (document.getElementById(targetId));
                if (!input) return;

                const isPassword = input.type === 'password';
                input.type = isPassword ? 'text' : 'password';

                const eyeOpen = btn.querySelector('.eye-open');
                const eyeClosed = btn.querySelector('.eye-closed');

                if (eyeOpen instanceof HTMLElement) {
                    eyeOpen.style.display = isPassword ? 'none' : '';
                }
                if (eyeClosed instanceof HTMLElement) {
                    eyeClosed.style.display = isPassword ? '' : 'none';
                }

        /** @type {HTMLElement} */ (btn).setAttribute(
                    'aria-label',
                    isPassword ? 'Ocultar senha' : 'Mostrar senha'
                );
            });
        });
    }

    /* =========================================
       REGISTRO — Máscaras e Validação
       ========================================= */

    #initRegister() {
        const cpfCnpjInput = /** @type {HTMLInputElement | null} */ (document.getElementById('RegisterForm-cpf-cnpj'));
        const phoneInput = /** @type {HTMLInputElement | null} */ (document.getElementById('RegisterForm-phone'));
        const form = document.getElementById('create_customer');
        const cpfCnpjError = document.getElementById('cpf-cnpj-error');

        if (cpfCnpjInput) {
            cpfCnpjInput.addEventListener('input', () => {
                cpfCnpjInput.value = this.#maskCpfCnpj(cpfCnpjInput.value);
                this.#clearFieldError(cpfCnpjInput, cpfCnpjError);
            });
        }

        if (phoneInput) {
            phoneInput.addEventListener('input', () => {
                phoneInput.value = this.#maskPhone(phoneInput.value);
            });
        }

        if (form) {
            form.addEventListener('submit', (e) => {
                if (!this.#validateCpfCnpj(cpfCnpjInput, cpfCnpjError)) {
                    e.preventDefault();
                }
            });
        }
    }

    /* --- Máscara CPF/CNPJ --- */
    /** @param {string} value */
    #maskCpfCnpj(value) {
        const digits = value.replace(/\D/g, '').slice(0, 14);

        if (digits.length <= 11) {
            /* CPF: 000.000.000-00 */
            return digits
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        }
        /* CNPJ: 00.000.000/0000-00 */
        return digits
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1/$2')
            .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }

    /* --- Máscara Telefone --- */
    /** @param {string} value */
    #maskPhone(value) {
        const digits = value.replace(/\D/g, '').slice(0, 11);

        if (digits.length <= 10) {
            /* Fixo: (00) 0000-0000 */
            return digits
                .replace(/(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
        }
        /* Celular: (00) 00000-0000 */
        return digits
            .replace(/(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
    }

    /* --- Validação CPF --- */
    /** @param {string} cpf */
    #validateCpf(cpf) {
        const digits = cpf.replace(/\D/g, '');
        if (digits.length !== 11) return false;

        /* Rejeita sequências iguais (111.111.111-11 etc.) */
        if (/^(\d)\1{10}$/.test(digits)) return false;

        /* Dígito verificador 1 */
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(digits.charAt(i)) * (10 - i);
        }
        let remainder = (sum * 10) % 11;
        if (remainder === 10) remainder = 0;
        if (remainder !== parseInt(digits.charAt(9))) return false;

        /* Dígito verificador 2 */
        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(digits.charAt(i)) * (11 - i);
        }
        remainder = (sum * 10) % 11;
        if (remainder === 10) remainder = 0;
        if (remainder !== parseInt(digits.charAt(10))) return false;

        return true;
    }

    /* --- Validação CNPJ --- */
    /** @param {string} cnpj */
    #validateCnpj(cnpj) {
        const digits = cnpj.replace(/\D/g, '');
        if (digits.length !== 14) return false;

        /* Rejeita sequências iguais */
        if (/^(\d)\1{13}$/.test(digits)) return false;

        /** @type {number[]} */
        const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        /** @type {number[]} */
        const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

        /* Dígito verificador 1 */
        let sum = 0;
        for (let i = 0; i < 12; i++) {
            sum += parseInt(digits.charAt(i)) * (weights1[i] ?? 0);
        }
        let remainder = sum % 11;
        const d1 = remainder < 2 ? 0 : 11 - remainder;
        if (d1 !== parseInt(digits.charAt(12))) return false;

        /* Dígito verificador 2 */
        sum = 0;
        for (let i = 0; i < 13; i++) {
            sum += parseInt(digits.charAt(i)) * (weights2[i] ?? 0);
        }
        remainder = sum % 11;
        const d2 = remainder < 2 ? 0 : 11 - remainder;
        if (d2 !== parseInt(digits.charAt(13))) return false;

        return true;
    }

    /* --- Validação unificada CPF/CNPJ --- */
    /**
     * @param {HTMLInputElement | null} input
     * @param {HTMLElement | null} errorEl
     */
    #validateCpfCnpj(input, errorEl) {
        if (!input) return true;

        const digits = input.value.replace(/\D/g, '');

        if (digits.length === 0) {
            this.#showFieldError(input, errorEl, 'CPF/CNPJ é obrigatório.');
            return false;
        }

        if (digits.length <= 11) {
            if (!this.#validateCpf(digits)) {
                this.#showFieldError(input, errorEl, 'CPF inválido. Verifique os dígitos.');
                return false;
            }
        } else {
            if (!this.#validateCnpj(digits)) {
                this.#showFieldError(input, errorEl, 'CNPJ inválido. Verifique os dígitos.');
                return false;
            }
        }

        return true;
    }

    /**
     * @param {HTMLInputElement | null} input
     * @param {HTMLElement | null} errorEl
     * @param {string} message
     */
    #showFieldError(input, errorEl, message) {
        if (input) {
            input.classList.add('field--error');
            input.setAttribute('aria-invalid', 'true');
        }
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.style.display = '';
        }
    }

    /**
     * @param {HTMLInputElement | null} input
     * @param {HTMLElement | null} errorEl
     */
    #clearFieldError(input, errorEl) {
        if (input) {
            input.classList.remove('field--error');
            input.removeAttribute('aria-invalid');
        }
        if (errorEl) {
            errorEl.textContent = '';
            errorEl.style.display = 'none';
        }
    }

    /* =========================================
       LOGIN — Toggle recuperação de senha
       ========================================= */

    #initLogin() {
        const loginForm = document.getElementById('customer-login-form');
        const recoverForm = document.getElementById('customer-recover-form');
        const showRecoverBtn = document.getElementById('show-recover-form');
        const hideRecoverBtn = document.getElementById('hide-recover-form');

        if (!loginForm || !recoverForm) return;

        /* Check if URL has #recover hash (from Shopify redirect after recovery request) */
        if (window.location.hash === '#recover') {
            loginForm.style.display = 'none';
            recoverForm.style.display = '';
        }

        if (showRecoverBtn) {
            showRecoverBtn.addEventListener('click', (e) => {
                e.preventDefault();
                loginForm.style.display = 'none';
                recoverForm.style.display = '';
        /** @type {HTMLInputElement | null} */ (recoverForm.querySelector('input[type="email"]'))?.focus();
            });
        }

        if (hideRecoverBtn) {
            hideRecoverBtn.addEventListener('click', (e) => {
                e.preventDefault();
                recoverForm.style.display = 'none';
                loginForm.style.display = '';
        /** @type {HTMLInputElement | null} */ (loginForm.querySelector('input[type="email"]'))?.focus();
            });
        }
    }

    /* =========================================
       ENDEREÇOS — Toggle formulários
       ========================================= */

    #initAddresses() {
        /* Toggle novo endereço */
        const toggleNewBtn = document.getElementById('toggle-new-address');
        const newAddressForm = document.getElementById('new-address-form');

        if (toggleNewBtn && newAddressForm) {
            toggleNewBtn.addEventListener('click', () => {
                const isHidden = newAddressForm.style.display === 'none';
                newAddressForm.style.display = isHidden ? '' : 'none';
                toggleNewBtn.textContent = isHidden ? 'Cancelar' : 'Adicionar novo endereço';
            });
        }

        /* Toggle edição de endereços existentes */
        document.querySelectorAll('[data-toggle-edit-address]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const addressId = /** @type {HTMLElement} */ (btn).dataset.toggleEditAddress;
                const editForm = document.getElementById(`edit-address-${addressId}`);
                if (editForm) {
                    const isHidden = editForm.style.display === 'none';
                    editForm.style.display = isHidden ? '' : 'none';
                }
            });
        });

        /* Confirmação de exclusão */
        document.querySelectorAll('[data-delete-address]').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                if (!confirm('Tem certeza que deseja excluir este endereço?')) {
                    e.preventDefault();
                }
            });
        });
    }
}

/* Init on DOM ready */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CustomerAccounts());
} else {
    new CustomerAccounts();
}
